import { useCallback, useMemo } from 'react';

import { useUser } from '@clerk/nextjs';
import { useSessionStorage } from '@uidotdev/usehooks';

import { useAppParams } from '@/hooks/use-app-params';
import { type PortfolioTrialArm, readPortfolioTrialArm } from '@/lib/feature-flags/portfolio-trial-arm-stamp';

import { Analytics } from './index';

/**
 * Signed-out renders send no identity at all, so every field is optional. Spelling that out keeps
 * `userData.user_id` readable without narrowing a union at each call site.
 */
interface AnalyticsUserData {
  email?: string;
  external_id?: string | null;
  first_name?: string | null;
  last_name?: string | null;
  portfolio_trial_start_arm?: PortfolioTrialArm;
  user_id?: string;
}

export function useAnalytics() {
  const { user, isLoaded } = useUser();
  const appParams = useAppParams();
  const [_, setSourceEvent] = useSessionStorage('__source_event', {});

  const userData = useMemo<AnalyticsUserData>(() => {
    if (!(isLoaded && user)) {
      return {};
    }

    // The trial-start arm rides on EVERY event, not just the portfolio ones: three of the five
    // pre-registered guardrails (externship enrolls, week-1 submission, refunds) live outside the
    // portfolio funnel and still have to be read per arm. Sourced from the Clerk stamp, never from
    // a live flag read — a rollout change moving users between arms is the bug the stamp exists to
    // prevent, and reintroducing it here would corrupt every segmented read at once.
    const portfolioTrialArm = readPortfolioTrialArm(user.unsafeMetadata);

    return {
      user_id: user.id,
      external_id: user.externalId,
      email: user.emailAddresses?.[0]?.emailAddress,
      first_name: user.firstName,
      last_name: user.lastName,
      ...(portfolioTrialArm == null ? {} : { portfolio_trial_start_arm: portfolioTrialArm }),
    };
  }, [isLoaded, user]);

  const appParamsData = useMemo(
    () =>
      Object.fromEntries(
        Object.entries({
          externship_id: appParams.externshipId,
          externship_module_id: appParams.moduleId,
          externship_step_id: appParams.stepId,
          externship_substep_id: appParams.substepId,
        }).filter(([_, value]) => value),
      ),
    [appParams.substepId, appParams.stepId, appParams.moduleId, appParams.externshipId],
  );

  const track = useCallback(
    (event: string, properties?: Record<string, unknown>, callback?: () => void) => {
      Analytics.track(
        event,
        {
          ...appParamsData,
          ...userData,
          ...properties,
          timestamp: new Date().toISOString(),
        },
        undefined,
        callback,
      );
    },
    [userData, appParamsData],
  );

  const page = useCallback(
    (page: string, properties?: Record<string, unknown>) => {
      Analytics.page(page, {
        ...appParamsData,
        ...properties,
        ...userData,
        timestamp: new Date().toISOString(),
      });
    },
    [userData, appParamsData],
  );

  const trackSourceEvent = useCallback(
    (event: string, properties?: Record<string, unknown>) => {
      setSourceEvent({
        source_event: event,
        ...properties,
      });
    },
    [setSourceEvent],
  );

  const identify = useCallback(
    (properties?: Record<string, unknown>) => {
      // Do not merge route-scoped appParamsData into identify — Segment persists
      // those keys into ajs_user_traits and replays them as stale profile traits.
      Analytics.identify(userData.user_id, {
        ...userData,
        ...properties,
      });
    },
    [userData],
  );

  return { track, page, identify, trackSourceEvent };
}
